import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import parse from "html-react-parser";

import icon_mastercard from "../images/payments/mastercard.svg";
import icon_visa from "../images/payments/visa.svg";
import icon_amex from "../images/payments/amex.svg";
import icon_twint from "../images/payments/twint.svg";
import icon_postfinance from "../images/payments/postfinance.svg";
import icon_paypal from "../images/payments/paypal.svg";
import icon_printer from "../images/payments/printer.svg";
import icon_mail from "../images/payments/mail.svg";
import icon_invoice from "../images/payments/invoice.svg";

import Header from "./components/Header";
import Footer from "./components/Footer";

const Checkout = () => {
    const [orderId] = useState(sessionStorage.getItem("orderId"));
    const [token] = useState(sessionStorage.getItem("token"));

    const [order, setOrder] = useState({});
    const [tickets, setTickets] = useState([]);
    const [payment, setPayment] = useState({ paymentMethodId: 0, deliveryMethodId: 0, insurance: false });
    const [orderInsurancePrice, setOrderInsurancePrice] = useState(0);
    const [isAgbChecked, setIsAgbChecked] = useState(false);
    const [submitError, setSubmitError] = useState("");
    const [orderExpired, setOrderExpired] = useState(false);
    const [promotioncode, setPromotioncode] = useState("");
    const [addedPromotionCodes, setAddedPromotionCodes] = useState([]);
    const [promotionError, setPromotionError] = useState({ text: "", valid: false });
    const [resources, setResources] = useState({});

    const navigate = useNavigate();
    const baseUrl = process.env.REACT_APP_BASEURL_API;
    const { language } = useParams();
    const languageId = { de: 1, fr: 2, en: 3, it: 4 }[language] ?? 1;

    const isButtonFinishOrderDisabled = payment.paymentMethodId === 0 || !isAgbChecked;

    useEffect(() => {
        if (!orderId) {
            navigate(`/${language}/participant`);
            return;
        }
        axios.defaults.headers.common["Authorization"] = "Bearer " + token;
        requestResources();
        loadOrder(orderId);
        getOrderInsurance();
    }, []);

    useEffect(() => {
        requestResources();
    }, [language]);

    useEffect(() => {
        if (order && Array.isArray(order.tickets)) {
            const codes = [];
            order.tickets.forEach(ticket => {
                if (ticket.promotionCode && !codes.includes(ticket.promotionCode)) {
                    codes.push(ticket.promotionCode);
                }
            });
            if (codes.length > 0 && (codes.length !== addedPromotionCodes.length || !codes.every(c => addedPromotionCodes.includes(c)))) {
                setAddedPromotionCodes(codes);
            }
        }
    }, [order]);

    const requestResources = async () => {
        try {
            const res = await axios.get(`form/resources/${language}`);
            setResources(res.data?.checkOut ?? {});
        } catch (error) {
            console.error(error);
        }
    };

    const getOrderByOrderId = async (id) => {
        const res = await axios.get(`${baseUrl}/ShopBasket/Order/${id}`);
        return res.data;
    };

    const loadOrder = async (id) => {
        try {
            const updatedOrder = await getOrderByOrderId(id);
            const bookedTickets = updatedOrder.tickets ?? [];

            const updatedTickets = await Promise.all(
                bookedTickets.map(async (ticket) => {
                    try {
                        const [typeRes, infosRes] = await Promise.all([
                            axios.get(`${baseUrl}/TicketType/${ticket.ticketTypeId}`),
                            axios.get(`${baseUrl}/TicketType/${ticket.ticketTypeId}/Infos`),
                        ]);
                        const infos = infosRes.data.ticketTypeInfos ?? [];
                        const info = infos.find(i => i.languageId === languageId) ?? infos.find(i => i.languageId === 1) ?? infos[0];
                        return { ...ticket, ticketType: typeRes.data, info };
                    } catch {
                        return ticket;
                    }
                })
            );

            setPayment(prev => ({
                ...prev,
                deliveryMethodId: updatedOrder.deliveryMethodId,
                paymentMethodId: updatedOrder.paymentMethodId,
                insurance: updatedOrder.totalInsuranceAmount > 0,
            }));
            setOrder(updatedOrder);
            setTickets(updatedTickets);
        } catch (error) {
            setOrderExpired(true);
        }
    };

    const changeDeliveryType = async (deliveryMethodId) => {
        try {
            await axios.put(`${baseUrl}/ShopBasket/Order/${orderId}/DeliveryMethod/${deliveryMethodId}`);
            await loadOrder(orderId);
        } catch (error) {
            setOrderExpired(true);
        }
    };

    const changePaymentType = async (paymentMethodId) => {
        try {
            await axios.put(`${baseUrl}/ShopBasket/Order/${orderId}/PaymentType/${paymentMethodId}`);
            await loadOrder(orderId);
        } catch (error) {
            setOrderExpired(true);
        }
    };

    const addPromotionToOrder = async () => {
        try {
            await axios.put(`${baseUrl}/ShopBasket/Order/${orderId}/PromotionCode/${promotioncode}`);
            setPromotionError({ text: `${resources.PromocodeSuccess} (${promotioncode})`, valid: true });
            setAddedPromotionCodes(current => [...current, promotioncode]);
            await loadOrder(orderId);
            setPromotioncode("");
        } catch {
            setPromotionError({ text: resources.PromocodeError, valid: false });
        }
    };

    const getOrderInsurance = async () => {
        try {
            const res = await axios.get(`${baseUrl}/ShopBasket/Order/${orderId}/Insurance`);
            setOrderInsurancePrice(res.data);
        } catch (error) {
            console.error(error);
        }
    };

    const setOrderInsurance = async () => {
        try {
            await axios.put(`${baseUrl}/ShopBasket/Order/${orderId}/Insurance`);
            await loadOrder(orderId);
        } catch (error) {
            console.error(error);
        }
    };

    const removeOrderInsurance = async () => {
        try {
            await axios.delete(`${baseUrl}/ShopBasket/Order/${orderId}/Insurance`);
            await loadOrder(orderId);
        } catch (error) {
            console.error(error);
        }
    };

    const confirmOrder = async () => {
        await axios.put(`${baseUrl}/ShopBasket/Order/${orderId}/Confirm`);
    };

    const startDatatrans = () => {

        let domain = "https://" + window.location.host;

        const redirectUrl = `${domain}/form/redirect/${language}/${orderId}`;
        const errorUrl = `${domain}/form/error/${language}/${orderId}`;
        const cancelUrl = `${domain}/form/cancel/${language}/${orderId}`;

        var baseRequestUrl = `https://datatrans.ticketino.com/Datatrans/${orderId}/DigitalSignature?redirectUrl=${redirectUrl}&errorUrl=${errorUrl}&cancelUrl=${cancelUrl}`;

        axios
            .get(baseRequestUrl)
            .then((res) => {

                var url = res.data.datatransPaymentRedirectUrl.replace("{0}", res.data.transactionId);

                window.location.href = url;
            })
            .catch((error) => {
                console.log(error);
            });
    };

    const onSubmit = async () => {
        setSubmitError("");
        try {
            await getOrderByOrderId(orderId);
        } catch {
            setOrderExpired(true);
            return;
        }

        const datatransProviders = [1, 7, 8, 9, 10, 14];
        if (order?.totalPrice > 0 && datatransProviders.includes(payment.paymentMethodId)) {
            startDatatrans();
        } else {
            try {
                if (order?.totalPrice === 0 || order?.totalPrice == null) {
                    await axios.put(`${baseUrl}/ShopBasket/Order/${orderId}/PaymentType/5`);
                }
                await confirmOrder();
                sessionStorage.removeItem("companionCount");
                sessionStorage.removeItem("pendingTickets");
                sessionStorage.removeItem("buyerInfo");
                sessionStorage.removeItem("mainBuyerInfo");
                sessionStorage.removeItem("orderId");
                sessionStorage.removeItem("token");
                navigate(`/${language}/confirmation/${orderId}`);
            } catch (error) {
                console.error(error);
                setSubmitError(resources?.ErrorOccurred);
            }
        }
    };

    const handleLanguageChange = (lang) => {
        navigate(`/${lang}/checkout`);
    };

    const mapTickets = () => {
        if (!tickets || tickets.length === 0) return null;
        const sorted = [...tickets].sort((a, b) => a.id - b.id);
        return sorted.map((tt) => (
            <div key={tt.id} className="d-flex justify-content-between align-items-center mt-3">
                <label className="mb-0">1x {tt.info?.name ?? tt.ticketType?.name}</label>
                <label className="text-nowrap mb-0">
                    {tt.ticketType?.currency ?? order.currency} {(tt.ticketTypePrice ?? 0).toFixed(2)}
                </label>
            </div>
        ));
    };

    return (
        <>
            <div id="datatrans-form-placeholder"></div>
            <Header resources={resources} title={resources?.CheckoutTitle} language={language} onLanguageChange={handleLanguageChange} containerClass="page-container-medium" />

            {orderExpired && (
                <div className="container page-container">
                    <div className="mt-5 page-container-smaller text-center">
                        <div className="alert alert-warning" role="alert">
                            {resources?.OrderExpiredMessage}
                        </div>
                        <button className="btn btn-custom mt-3" onClick={() => navigate(`/${language}/participant`)}>
                            {resources?.Back}
                        </button>
                    </div>
                </div>
            )}

            {!orderExpired && <div className="container page-container">
                <div className="mt-4 page-container-smaller">

                    {order.totalPrice > 0 && (
                        <div className="mt-4 mb-5">
                            <p className="subtitle-bold mb-2">{resources?.PromotionCodeTitle}</p>
                            <p>{resources?.PromocodeDescription}</p>
                            <div className="row">
                                <div className="col-md-4 col-8 mb-3">
                                    <input className="form-control" placeholder={resources?.PromoCodePlaceholder} value={promotioncode} onChange={(e) => setPromotioncode(e.target.value)} />
                                </div>
                                <div className="col-md-4">
                                    <button className="btn btn-custom" disabled={!promotioncode} onClick={addPromotionToOrder}>{resources?.Redeem}</button>
                                </div>
                            </div>
                            {promotionError.text && (
                                <p className={promotionError.valid ? "text-success" : "text-danger"}>{promotionError.text}</p>
                            )}
                        </div>
                    )}

                    {order.totalPrice > 0 && (
                        <>
                            <p className="subtitle-bold mb-3">{resources?.DeliveryOption}</p>
                            <div className="mb-5">
                                {[
                                    { id: 1, icon: icon_printer, label: resources?.PrintAtHome, desc: resources?.PrintAtHomeDescription },
                                    { id: 2, icon: icon_mail, label: resources?.MailDelivery, desc: resources?.MailDeliveryDesc },
                                ].map(({ id, icon, label, desc }) => (
                                    <div className="form-check mb-4" key={id}>
                                        <input
                                            className="form-check-input me-2"
                                            type="radio"
                                            name="deliveryMethod"
                                            id={`delivery-${id}`}
                                            checked={payment.deliveryMethodId === id}
                                            onChange={() => changeDeliveryType(id)}
                                        />
                                        <label className="d-inline payment-names" htmlFor={`delivery-${id}`}>
                                            <img src={icon} className="payment_icon" alt={label} />
                                            {label}
                                            {desc && <div className="payment-desc">{desc}</div>}
                                        </label>
                                    </div>
                                ))}
                            </div>

                            <p className="subtitle-bold mb-4">{resources?.PaymentOption}</p>
                            <div className="mb-5">
                                {[
                                    { id: 1, icon: icon_mastercard, label: resources?.MasterCard, desc: resources?.MasterCardDesc },
                                    { id: 7, icon: icon_visa, label: resources?.Visa, desc: resources?.VisaDesc },
                                    { id: 8, icon: icon_amex, label: resources?.AmericanExpress, desc: resources?.AmericanExpressDesc },
                                    { id: 10, icon: icon_paypal, label: resources?.PayPal, desc: resources?.PayPalDesc },
                                    { id: 14, icon: icon_twint, label: resources?.Twint, desc: resources?.TwintDesc },
                                    { id: 9, icon: icon_postfinance, label: resources?.PostFinance, desc: resources?.PostFinanceDesc },
                                    { id: 2, icon: icon_invoice, label: resources?.Invoice, desc: "" },
                                ].map(({ id, icon, label, desc }) => (
                                    <div className="form-check mb-4" key={id}>
                                        <input
                                            className="form-check-input me-2"
                                            type="radio"
                                            name="paymentMethod"
                                            id={`payment-${id}`}
                                            checked={payment.paymentMethodId === id}
                                            onChange={() => changePaymentType(id)}
                                        />
                                        <label className="d-inline payment-names" htmlFor={`payment-${id}`}>
                                            <img src={icon} className="payment_icon" alt={label} />
                                            {label}
                                            {desc && <div className="payment-desc">{desc}</div>}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}                 

                    {order.totalPrice > 0 && (
                        <div className="mb-5">
                            <p className="subtitle-bold mb-2">{resources?.TicketInsurance}</p>
                            <div className="agb-checkbox-row" onClick={() => payment.insurance ? removeOrderInsurance() : setOrderInsurance()}>
                                <input
                                    className="agb-checkbox"
                                    type="checkbox"
                                    checked={payment.insurance}
                                    onChange={() => {}}
                                />
                                <label className="insurance-text">
                                    {parse((orderInsurancePrice === 0
                                        ? resources?.TicketInsuranceDescGeneric
                                        : (resources?.TicketInsuranceDesc ?? "").replace("{@InsurancePrice}", orderInsurancePrice.toFixed(2))
                                    ) ?? "")}
                                </label>
                            </div>
                        </div>
                    )}

                    <p className="subtitle-bold mb-2">{resources?.Summary}</p>
                    <div className="summary-box mb-5">
                        {mapTickets()}

                        {addedPromotionCodes.length > 0 && (
                            <div className="d-flex justify-content-between mt-2">
                                <span>{resources?.PromotionCodes}</span>
                                <span>{addedPromotionCodes.join(", ")}</span>
                            </div>
                        )}
                        {payment.insurance && order.totalInsuranceAmount > 0 && (
                            <div className="d-flex justify-content-between mt-2">
                                <span>{resources?.TicketInsurance}</span>
                                <span>{order.currency} {order.totalInsuranceAmount?.toFixed(2)}</span>
                            </div>
                        )}
                        {order.deliverPaymentFee > 0 && (
                            <div className="d-flex justify-content-between mt-2">
                                <span>{resources?.DeliveryCosts}</span>
                                <span>{order.currency} {order.deliverPaymentFee?.toFixed(2)}</span>
                            </div>
                        )}
                        <hr />
                        <div className="d-flex justify-content-between fw-bold">
                            <span>{resources?.Total}</span>
                            <span>{order.currency} {order.totalPrice?.toFixed(2)}</span>
                        </div>
                    </div>

                    <div className="agb-checkbox-row mt-4" onClick={() => setIsAgbChecked(v => !v)}>
                        <input
                            type="checkbox"
                            className="agb-checkbox"
                            checked={isAgbChecked}
                            onChange={() => {}}
                        />
                        <label className="agb-text" dangerouslySetInnerHTML={{ __html: resources?.AGB }} />
                    </div>

                    {submitError && (
                        <div className="alert alert-danger mt-3" role="alert">{submitError}</div>
                    )}

                    <div className="row mt-5">
                        <div className="col-12 d-flex justify-content-between">
                            <button className="btn btn-custom" onClick={() => navigate(`/${language}/accommodation`)}>
                                {resources?.Back}
                            </button>
                            <button className="btn btn-custom" disabled={isButtonFinishOrderDisabled} onClick={onSubmit}>
                                {resources?.Buy}
                            </button>
                        </div>
                    </div>

                </div>
            </div>}
            <Footer language={language} />
        </>
    );
};

export default Checkout;
